import type { ReactElement } from 'react';
import { useCreateAccountMutation, AccountsDocument, type ICreateAccountMutation } from 'generated/graphql';
import { useFeedback } from 'components/technical/Feedback/UseFeedback.tsx';
import { getErrorDetails } from 'components/technical/errors/GraphQLAPIService.ts';
import InputGenieAccountDetailsStep from './InputGenieAccountDetailsStep';
import { defaultCreateAccountFormName, formDefaultSchema } from './CreateAccountDialog';
import type { FetchResult } from '@apollo/client';
import { Modal, ModalDialog, ModalOverflow } from '@mui/joy';
import { IBKR_VENUE } from 'components/venue/VenueData';

interface InteractiveBrokersConnectionProps {
  oauthParams: { token: string; verifier: string };
  onComplete: () => void;
}

interface FormData {
  name: string;
  description?: string;
}

const InteractiveBrokersConnection = ({ oauthParams, onComplete }: InteractiveBrokersConnectionProps): ReactElement => {
  const [createAccount] = useCreateAccountMutation({
    refetchQueries: [AccountsDocument],
  });
  const { showSuccessMessage } = useFeedback();

  return (
    <Modal open={true} onClose={onComplete}>
      <ModalOverflow>
        <ModalDialog size="md">
          <InputGenieAccountDetailsStep<FormData>
            title="Connect IBKR"
            defaultValues={{
              name: '',
            }}
            fields={[defaultCreateAccountFormName]}
            formSchema={formDefaultSchema}
            handleSubmit={async (data, onError, setError): Promise<void> => {
              let response: FetchResult<ICreateAccountMutation> | undefined = undefined;
              try {
                response = await createAccount({
                  variables: {
                    input: {
                      name: data.name.trim(),
                      venue: IBKR_VENUE,
                      secrets: {
                        request_token: oauthParams.token,
                        oauth_verifier: oauthParams.verifier,
                      },
                    },
                  },
                });
              } catch (error) {
                const details = getErrorDetails(error);
                if (details?.code === 'account_duplicate_name') {
                  setError('name', {
                    type: 'custom',
                    message: 'Account with the same name already exists',
                  });
                } else {
                  onError(error);
                }

                throw error;
              }

              if (response.data?.CreateAccount?.id) {
                showSuccessMessage('Interactive Brokers account connected successfully!');
                onComplete();
              } else {
                const err = new Error('Failed to create Interactive Brokers account');
                onError(err);
                throw err;
              }
            }}
          />
        </ModalDialog>
      </ModalOverflow>
    </Modal>
  );
};

export default InteractiveBrokersConnection;
